import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageLayout from '../../layouts/PageLayout';
import { useBranch } from '../../context/BranchContext';
import { useAuth } from '../../context/AuthProvider';
import { employeeAPI } from '../../services';
import { cloudinaryService } from '../../services/cloudinary';
import { toast } from 'react-toastify';

// Giới hạn THẬT của Telegram Bot API công khai khi bot upload tệp:
// ảnh 10MB, video 50MB. Chặn NGAY lúc chọn tệp (AC 10) để không tốn công nén + upload rồi mới lỗi.
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
const MAX_VIDEO_BYTES = 50 * 1024 * 1024;
const ACCEPTED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const FILE_ACCEPT = [...ACCEPTED_PHOTO_TYPES, ...ACCEPTED_VIDEO_TYPES].join(',');

const CONTENT_PREVIEW_LENGTH = 150;
const PAGE_SIZE = 10;

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
};

const formatDateTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('vi-VN', { hour12: false });
};

/**
 * Suy khoá object (R2 key / Cloudinary public_id) từ kết quả upload, để lưu vào lịch sử.
 * R2 trả { secure_url } — khoá là phần path sau domain. Cloudinary trả sẵn public_id.
 */
const extractStorageKey = (uploadResult) => {
  if (!uploadResult) return undefined;
  if (uploadResult.public_id) return uploadResult.public_id;
  const url = uploadResult.secure_url || uploadResult.url;
  if (!url) return undefined;
  try {
    return new URL(url).pathname.replace(/^\//, '');
  } catch (e) {
    return undefined;
  }
};

const validateFile = (file) => {
  const isVideo = file.type?.startsWith('video/');
  const isImage = file.type?.startsWith('image/');
  if (!isVideo && !isImage) {
    return 'Định dạng không hỗ trợ — chỉ nhận ảnh (JPEG/PNG/WebP) hoặc video (MP4/WebM/MOV).';
  }
  if (isImage && !ACCEPTED_PHOTO_TYPES.includes(file.type)) {
    return `Định dạng ảnh không hỗ trợ (${file.type || 'không rõ'}) — dùng JPEG, PNG hoặc WebP.`;
  }
  if (isVideo && !ACCEPTED_VIDEO_TYPES.includes(file.type)) {
    return `Định dạng video không hỗ trợ (${file.type || 'không rõ'}) — dùng MP4, WebM hoặc MOV.`;
  }
  if (isImage && file.size > MAX_PHOTO_BYTES) {
    return `Ảnh vượt 10MB (tệp đang chọn ${formatBytes(file.size)}) — Telegram không nhận.`;
  }
  if (isVideo && file.size > MAX_VIDEO_BYTES) {
    return `Video vượt 50MB (tệp đang chọn ${formatBytes(file.size)}) — Telegram không nhận.`;
  }
  return null;
};

const BroadcastNotificationPanel = () => {
  const { branches, selectedBranch: selectedBranchForFilter } = useBranch();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';

  const [activeTab, setActiveTab] = useState('compose');
  const [branchId, setBranchId] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Tệp đính kèm (AC 9-12)
  const fileInputRef = useRef(null);
  const [attachment, setAttachment] = useState(null); // { file?, previewUrl, kind, name, size, url?, key?, unavailable? }
  const [fileError, setFileError] = useState('');
  const [uploading, setUploading] = useState(false);

  // Lịch sử (AC 3-5)
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);
  // #175019 — bộ lọc chi nhánh của lịch sử lấy từ thanh trên đầu, không còn ô riêng.
  // (Ô "Phạm vi gửi" bên dưới KHÔNG phải bộ lọc — đó là dữ liệu của tin sắp gửi, nên giữ.)
  const filterBranchId = selectedBranchForFilter?.id || '';
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const composeRef = useRef(null);

  // Thu hồi object URL của bản xem trước cục bộ để không rò bộ nhớ.
  const releasePreview = useCallback((att) => {
    if (att?.previewUrl && att.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(att.previewUrl);
    }
  }, []);

  useEffect(() => {
    return () => releasePreview(attachment);
  }, [attachment, releasePreview]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(historyTotal / PAGE_SIZE)),
    [historyTotal],
  );

  const loadHistory = useCallback(
    async (page = 1) => {
      try {
        setHistoryLoading(true);
        const res = await employeeAPI.getBroadcastHistory({
          branch_id: filterBranchId || undefined,
          from: filterFrom || undefined,
          to: filterTo || undefined,
          page,
          page_size: PAGE_SIZE,
        });
        const data = res?.data ?? res;
        setHistory(data?.items ?? []);
        setHistoryTotal(data?.total ?? 0);
        setHistoryPage(data?.page ?? page);
      } catch (err) {
        toast.error(err?.message || 'Không tải được lịch sử thông báo');
      } finally {
        setHistoryLoading(false);
      }
    },
    [filterBranchId, filterFrom, filterTo],
  );

  useEffect(() => {
    if (isAdmin && activeTab === 'history') {
      loadHistory(1);
    }
  }, [isAdmin, activeTab, loadHistory]);

  if (!isAdmin) {
    return (
      <PageLayout>
        <div className="mc-page">
          <div className="mc-page-header">
            <div className="mc-page-header-title">
              <h1>Gửi thông báo Telegram</h1>
            </div>
          </div>
          <div className="alert alert-danger">
            Bạn không có quyền truy cập tính năng này. Chỉ ADMIN mới được sử dụng.
          </div>
        </div>
      </PageLayout>
    );
  }

  const handlePickFile = (e) => {
    const file = e.target.files?.[0];
    // Cho phép chọn lại đúng tệp vừa xoá.
    e.target.value = '';
    if (!file) return;

    const error = validateFile(file);
    if (error) {
      // AC 10: báo lỗi NGAY lúc chọn, KHÔNG cho vào bước xem trước.
      setFileError(error);
      toast.error(error);
      return;
    }
    setFileError('');
    releasePreview(attachment);
    setAttachment({
      file,
      previewUrl: URL.createObjectURL(file),
      kind: file.type.startsWith('video/') ? 'VIDEO' : 'PHOTO',
      name: file.name,
      size: file.size,
    });
  };

  const handleRemoveFile = () => {
    releasePreview(attachment);
    setAttachment(null);
    setFileError('');
  };

  const handleSend = async (e) => {
    e?.preventDefault?.();
    if (!message.trim()) {
      toast.error('Vui lòng nhập nội dung thông báo');
      return;
    }
    // AC 12: đã "Dùng lại" 1 tin có tệp nhưng tệp gốc mất → không âm thầm gửi thiếu tệp.
    if (attachment?.unavailable) {
      toast.error(
        'Tệp đính kèm không còn khả dụng — hãy chọn tệp khác hoặc xoá tệp trước khi gửi.',
      );
      return;
    }
    const targetLabel = branchId
      ? branches.find((b) => b.id === branchId)?.name ?? 'chi nhánh đã chọn'
      : 'tất cả chi nhánh';
    const attachmentLabel = attachment
      ? `\n\nTệp đính kèm: ${attachment.kind === 'VIDEO' ? '🎬 Video' : '🖼️ Ảnh'} ${attachment.name ?? ''}`
      : '';
    const confirmed = window.confirm(
      `Xác nhận gửi thông báo Telegram đến nhân viên active của ${targetLabel}?\n\nNội dung:\n${message.trim()}${attachmentLabel}`,
    );
    if (!confirmed) return;

    let attachmentPayload = {};
    try {
      setSending(true);
      setResult(null);

      if (attachment) {
        if (attachment.url) {
          // Tệp đến từ "Dùng lại" — đã nằm sẵn trên R2/Cloudinary, không upload lại.
          attachmentPayload = {
            attachment_type: attachment.kind,
            attachment_url: attachment.url,
            attachment_key: attachment.key,
          };
        } else {
          setUploading(true);
          let uploaded;
          try {
            uploaded = await cloudinaryService.uploadImage(attachment.file, {
              folder: 'broadcast-telegram',
            });
          } catch (uploadErr) {
            // AC 11: upload lỗi → KHÔNG enqueue, KHÔNG tạo lịch sử "thành công" giả.
            throw new Error(
              `Tải tệp đính kèm lên thất bại — chưa gửi thông báo nào. Chi tiết: ${
                uploadErr?.message || 'không rõ'
              }`,
            );
          } finally {
            setUploading(false);
          }
          const url = uploaded?.secure_url || uploaded?.url;
          if (!url) {
            throw new Error(
              'Tải tệp đính kèm lên thất bại (không nhận được URL) — chưa gửi thông báo nào.',
            );
          }
          attachmentPayload = {
            attachment_type: attachment.kind,
            attachment_url: url,
            attachment_key: extractStorageKey(uploaded),
          };
        }
      }

      const res = await employeeAPI.broadcastTelegram({
        message: message.trim(),
        branch_id: branchId || undefined,
        ...attachmentPayload,
      });
      const data = res?.data ?? res;
      setResult(data);
      toast.success(`Đã enqueue thông báo đến ${data?.sent_count ?? 0} nhân viên`);
      if (activeTab === 'history') {
        loadHistory(1);
      }
    } catch (err) {
      toast.error(err?.message || 'Gửi thông báo thất bại');
    } finally {
      setSending(false);
      setUploading(false);
    }
  };

  /**
   * AC 7 + AC 12 — "Dùng lại": điền lại nội dung + phạm vi + tệp đính kèm vào form soạn.
   * KHÔNG tự gửi (Phương án A, chốt #731598688) — admin xem lại rồi tự bấm Gửi.
   */
  const handleReuse = (item) => {
    setMessage(item.message ?? '');
    setBranchId(item.branch_id ?? '');
    setResult(null);
    setFileError('');
    releasePreview(attachment);

    if (item.attachment_url && item.attachment_type) {
      // Không dùng fetch()/HEAD để dò tệp còn sống: R2/Cloudinary là khác origin, CORS sẽ ném lỗi
      // ngay cả khi tệp VẪN CÒN → báo nhầm "không khả dụng" và chặn gửi oan. Thay vào đó để chính
      // thẻ <img>/<video> tải thật, `onError` mới là bằng chứng tệp hỏng/mất (AC 12).
      setAttachment({
        previewUrl: item.attachment_url,
        url: item.attachment_url,
        key: item.attachment_key ?? undefined,
        kind: item.attachment_type,
        name: item.attachment_key?.split('/').pop() ?? 'tệp đính kèm',
        unavailable: false,
      });
    } else {
      setAttachment(null);
    }

    setActiveTab('compose');
    setTimeout(() => {
      composeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  // AC 12: tệp gốc đã bị xoá khỏi R2/Cloudinary → chính thẻ img/video tải hỏng.
  // Chỉ áp cho tệp đến từ "Dùng lại" (có .url); tệp vừa chọn tay dùng blob: nên luôn tải được.
  const handleAttachmentLoadError = () => {
    setAttachment((prev) => {
      if (!prev || !prev.url || prev.unavailable) return prev;
      toast.warn('Tệp đính kèm của thông báo này không còn khả dụng');
      return { ...prev, unavailable: true };
    });
  };

  const renderAttachmentPreview = () => {
    if (!attachment) return null;
    return (
      <div className="border rounded p-2 mt-2">
        {attachment.unavailable ? (
          <div className="alert alert-warning mb-2 py-2 px-3 small">
            ⚠️ <strong>Tệp đính kèm không còn khả dụng</strong> — tệp gốc đã bị xoá khỏi kho lưu
            trữ. Hãy chọn tệp khác hoặc xoá tệp trước khi gửi.
          </div>
        ) : attachment.kind === 'VIDEO' ? (
          <video
            src={attachment.previewUrl}
            controls
            className="w-100 rounded"
            style={{ maxHeight: 220, background: '#000' }}
            onError={handleAttachmentLoadError}
          />
        ) : (
          <img
            src={attachment.previewUrl}
            alt="Xem trước tệp đính kèm"
            className="rounded"
            style={{ maxHeight: 220, maxWidth: '100%', objectFit: 'contain' }}
            onError={handleAttachmentLoadError}
          />
        )}
        <div className="d-flex align-items-center justify-content-between mt-2 flex-wrap gap-2">
          <small className="text-muted text-truncate" style={{ maxWidth: '60%' }}>
            {attachment.kind === 'VIDEO' ? '🎬' : '🖼️'} {attachment.name}
            {attachment.size ? ` · ${formatBytes(attachment.size)}` : ''}
          </small>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending}
            >
              Thay tệp khác
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={handleRemoveFile}
              disabled={sending}
            >
              Xoá tệp
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderHistory = () => (
    <div className="card mb-4">
      <div className="card-header">
        <h5 className="mb-0">🕘 Lịch sử thông báo đã gửi</h5>
      </div>
      <div className="card-body">
        <div className="row g-2 mb-3">
          <div className="col-md-3">
            <label className="form-label small mb-1">Từ ngày</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={filterFrom}
              onChange={(e) => setFilterFrom(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label small mb-1">Đến ngày</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={filterTo}
              onChange={(e) => setFilterTo(e.target.value)}
            />
          </div>
          <div className="col-md-2 d-flex align-items-end">
            <button
              type="button"
              className="btn btn-sm btn-primary w-100"
              onClick={() => loadHistory(1)}
              disabled={historyLoading}
            >
              Lọc
            </button>
          </div>
        </div>

        {historyLoading ? (
          <div className="text-center py-4 text-muted">
            <span className="spinner-border spinner-border-sm me-2" />
            Đang tải lịch sử...
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-4 text-muted">
            Chưa có lần gửi thông báo nào khớp bộ lọc.
          </div>
        ) : (
          <>
            <div className="d-flex flex-column gap-2">
              {history.map((item) => {
                const isLong =
                  (item.message?.length ?? 0) > CONTENT_PREVIEW_LENGTH;
                const expanded = expandedId === item.id;
                return (
                  <div key={item.id} className="border rounded p-2">
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                      <div className="small text-muted">
                        🕒 {formatDateTime(item.created_at)} · 📍{' '}
                        {item.branch_name ?? 'Tất cả chi nhánh'} · 👤{' '}
                        {item.sent_by_name ?? 'Không rõ'}
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleReuse(item)}
                      >
                        ♻️ Dùng lại
                      </button>
                    </div>
                    <div className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                      {isLong && !expanded
                        ? `${item.message.slice(0, CONTENT_PREVIEW_LENGTH)}…`
                        : item.message}
                    </div>
                    {isLong && (
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={() => setExpandedId(expanded ? null : item.id)}
                      >
                        {expanded ? 'Thu gọn' : 'Xem đầy đủ'}
                      </button>
                    )}
                    <div className="mt-2 small text-muted">
                      📨 Đã đưa vào hàng đợi: <strong>{item.enqueued_count}</strong>/
                      {item.total_active} nhân viên active
                      {item.attachment_type && (
                        <span className="ms-2">
                          · {item.attachment_type === 'VIDEO' ? '🎬 Video' : '🖼️ Ảnh'} đính kèm
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3">
              <small className="text-muted">
                Trang {historyPage}/{totalPages} · {historyTotal} lần gửi
              </small>
              <div className="btn-group btn-group-sm">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={historyPage <= 1 || historyLoading}
                  onClick={() => loadHistory(historyPage - 1)}
                >
                  ← Trước
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={historyPage >= totalPages || historyLoading}
                  onClick={() => loadHistory(historyPage + 1)}
                >
                  Sau →
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <PageLayout>
      <div className="mc-page">
        <div className="mc-page-header">
          <div className="mc-page-header-title">
            <h1>Gửi thông báo Telegram</h1>
            <p>Gửi thông báo đến toàn bộ nhân viên active đã liên kết Telegram</p>
          </div>
        </div>

        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${activeTab === 'compose' ? 'active' : ''}`}
              onClick={() => setActiveTab('compose')}
            >
              📢 Soạn thông báo
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => setActiveTab('history')}
            >
              🕘 Lịch sử thông báo
            </button>
          </li>
        </ul>

        {activeTab === 'history' ? (
          renderHistory()
        ) : (
          <div className="row">
            <div className="col-lg-6">
              <div className="card mb-4" ref={composeRef}>
                <div className="card-header">
                  <h5 className="mb-0">📢 Soạn thông báo</h5>
                </div>
                <div className="card-body">
                  <form onSubmit={handleSend}>
                    <div className="mb-3">
                      <label className="form-label">Phạm vi gửi</label>
                      <select
                        className="form-select"
                        value={branchId}
                        onChange={(e) => setBranchId(e.target.value)}
                        disabled={sending}
                      >
                        <option value="">Tất cả chi nhánh</option>
                        {branches.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name}
                          </option>
                        ))}
                      </select>
                      <small className="form-text text-muted d-block mt-1">
                        Chỉ gửi đến nhân viên ACTIVE đã liên kết Telegram.
                      </small>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Nội dung thông báo <span className="text-danger">*</span></label>
                      <textarea
                        className="form-control"
                        rows={6}
                        maxLength={1000}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        disabled={sending}
                        placeholder="Nhập nội dung thông báo gửi đến nhân viên..."
                        required
                      />
                      <small className="form-text text-muted d-block mt-1">
                        {message.length}/1000 ký tự. Tin nhắn sẽ được gửi kèm tiêu đề nhận diện chi nhánh.
                      </small>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Ảnh hoặc video đính kèm (không bắt buộc)</label>
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="form-control"
                        accept={FILE_ACCEPT}
                        onChange={handlePickFile}
                        disabled={sending}
                      />
                      <small className="form-text text-muted d-block mt-1">
                        Chọn 1 ảnh (JPEG/PNG/WebP, tối đa 10MB) HOẶC 1 video (MP4/WebM/MOV, tối đa 50MB).
                      </small>
                      {fileError && (
                        <div className="alert alert-danger py-2 px-3 mt-2 mb-0 small">
                          {fileError}
                        </div>
                      )}
                      {renderAttachmentPreview()}
                    </div>

                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={sending || !message.trim()}
                    >
                      {sending ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          {uploading ? 'Đang tải tệp lên...' : 'Đang gửi...'}
                        </>
                      ) : (
                        'Gửi thông báo'
                      )}
                    </button>
                  </form>
                </div>
              </div>

              {result && (
                <div className="card mb-4 border-success">
                  <div className="card-header bg-success text-white">
                    <h5 className="mb-0">✅ Kết quả gửi</h5>
                  </div>
                  <div className="card-body">
                    <p className="mb-1">
                      <strong>Đã đưa vào hàng đợi:</strong> {result.sent_count} nhân viên
                    </p>
                    <p className="mb-0 text-muted">
                      <strong>Nhân viên active:</strong> {result.total_active} người
                      {result.total_active > result.sent_count && (
                        <span className="ms-2">
                          ({result.total_active - result.sent_count} chưa liên kết Telegram)
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="col-lg-6">
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">ℹ️ Hướng dẫn</h5>
                </div>
                <div className="card-body">
                  <ul className="mb-0 ps-3 text-muted small">
                    <li className="mb-2">Chỉ nhân viên có trạng thái <strong>Active</strong> và đã liên kết Telegram mới nhận được thông báo.</li>
                    <li className="mb-2">Tin nhắn được gửi qua hàng đợi (queue) để tránh quá tải — không gửi đồng loạt ngay lập tức.</li>
                    <li className="mb-2">Tin nhắn tự động thêm tiêu đề nhận diện chi nhánh ở đầu.</li>
                    <li className="mb-2">Ảnh/video đính kèm: Telegram giới hạn ảnh 10MB, video 50MB; chữ đi kèm tệp tối đa 1024 ký tự.</li>
                    <li>Không thể thu hồi sau khi gửi — hãy kiểm tra kỹ nội dung trước.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default BroadcastNotificationPanel;
